/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import PropTypes from 'prop-types';

/* Importing the components from the Material UI library. */
import { Button, Grid } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

/* Importing the getEncounterAll and deleteEncounter functions from the encounterSlice.js file. */
import { deleteEncounter, searchEncounter } from '../../features/encounter/encounterSlice';

/* Importing components. */
import EncounterAdd from './Encounter.section.add';
import EncounterDetails from './Encounter.section.details';
import MaterialTable from '../../components/MaterialTable_noCard';

/* Importing functions from the utils folder. */
import deleteResourceHandler from '../../utils/deleteResourceHandler';
import { fEncounters } from '../../utils/Encounter/Encounter.utils.format';

/* Defining the propTypes for the EncounterList component. */
EncounterList.propTypes = {
  patientID: PropTypes.string,
};

/**
 * Renders a list of encounters for a patient.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.patientID - The ID of the patient.
 * @returns {JSX.Element} The rendered component.
 */
export default function EncounterList(props) {
  const { patientID } = props;

  const dispatch = useDispatch();

  /* Using the useSelector hook to get the encounter state from the Redux store. */
  const { Encounters, searchResults } = useSelector((state) => state.encounter);

  /* Setting the states of objects. */
  const [isLoading, setIsLoading] = useState(true);
  const [EncountersFormatted, setEncountersFormatted] = useState([]);

  /**
   * Format the encounter data to be used in the table.
   */
  const formatEncounters = useCallback(() => {
    const encountersSearch = searchResults.map((p) => p.resource);
    const EncountersFiltered = Encounters.filter((p) => p.subject?.reference === `Patient/${patientID}`);

    // Combine the two arrays
    const combinedArray = encountersSearch.concat(EncountersFiltered);

    // Create an object to store the encounters by ID with the highest version ID
    const encountersById = {};

    combinedArray.forEach((encounter) => {
      const { id } = encounter;

      if (!encountersById[id] || encounter.meta.versionId > encountersById[id].meta.versionId) {
        encountersById[id] = encounter;
      }
    });

    // Convert the object back to an array
    const resultArray = Object.values(encountersById);

    const encountersArray = fEncounters(resultArray);
    setEncountersFormatted(encountersArray);
    setIsLoading(false);
  }, [Encounters, patientID, searchResults]);

  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(() => {
    dispatch(searchEncounter({ query: `subject=Patient/${patientID}` }));
  }, [dispatch, patientID]);

  const [isEdit, setIsEdit] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [encounterID, setEncounterID] = useState();

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Calling the formatEncounters function when the component mounts and when the encounterState changes. */
  useEffect(() => {
    formatEncounters();
  }, [formatEncounters]);


  /**
   * Navigates to the edit page for the encounter with the given _id.
   */
  const readEncounterHandler = (_id) => {
    setEncounterID(_id);
    setIsDetailsOpen(true);
  };

  const updateEncounterHandler = (_id) => {
    setIsEdit(true);
    setEncounterID(_id);
    setIsAddOpen(true);
  };

  const handleClickOpen = () => {
    setIsEdit(false);
    setIsAddOpen(true);
  };

  const deleteEncounterHandler = async (id) => {
    const deleted = deleteResourceHandler({
      dispatch,
      deleteAction: deleteEncounter,
      id,
      successMessage: 'Encounter deleted successfully!',
    });

    if (deleted) {
      setTimeout(() => {
        dispatch(searchEncounter({ query: `subject=Patient/${patientID}` }));
      }, 1000);
    }
  };

  /* Creating a table with the following columns: ID, Last Name, Given Name, Suffix, Gender, and
  Birthday. */
  const columns = [
    {
      title: 'ID',
      field: 'id',
      defaultSort: 'desc',
    },
    {
      title: 'Class',
      field: 'class',
    },
    {
      title: 'Status',
      field: 'status',
    },
    {
      title: 'Start',
      field: 'period_start',
    },
    {
      title: 'End',
      field: 'period_end',
    },
  ];

  /**
   * Render the table of encounters.
   */
  const renderTable = () => (
    <MaterialTable
      onRowClick={(event, rowData) => readEncounterHandler(rowData.id)}
      options={{
        search: true,
        exportButton: true,
        exportFileName: moment().format('YYYY-MM-DD HH:mm:ss'),
        actionsColumnIndex: -1,
        sorting: true,
      }}
      actions={[
        {
          icon: () => <EditIcon />,
          tooltip: 'Edit Encounter',
          onClick: (event, rowData) => updateEncounterHandler(rowData.id),
        },
        {
          icon: () => <DeleteIcon />,
          tooltip: 'Delete Encounter',
          onClick: (event, rowData) => deleteEncounterHandler(rowData.id),
        },
      ]}
      title={''}
      isLoading={isLoading}
      columns={columns}
      data={EncountersFormatted}
    />
  );

  return (
    <>
      <Button variant="outlined" onClick={handleClickOpen}>
        Add Encounter
      </Button>
      <Grid container>
        <Grid item xs={12}>
          {renderTable()}
        </Grid>
      </Grid>
      <EncounterAdd
        isEdit={isEdit}
        isAddOpen={isAddOpen}
        encounterID={encounterID}
        patientID={patientID}
        setIsAddOpen={setIsAddOpen}
      />
      <EncounterDetails
        isDetailsOpen={isDetailsOpen}
        encounterID={encounterID}
        patientID={patientID}
        setIsDetailsOpen={setIsDetailsOpen}
      />
    </>
  );
}
